import { Flex, Paper, Rating, Text, TextInput } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useState, useEffect } from "react";
import { FullRatingIcon, RatingIcon } from "../../shared/svg";
import useReportStore from "../../store/useReportStore";
import { useTranslation } from "react-i18next";

interface LikeResponseProps {
  report_id: number;
  section: string;
}

export const LikeResponse = ({ report_id, section }: LikeResponseProps) => {
  const matches = useMediaQuery("(max-width: 950px)");
  const { t } = useTranslation();

  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [lastFeedback, setLastFeedback] = useState<any>(null);

  const {
    sendReportFeedback,
    getFeedbacksBySection,
    fetchReportFeedback,
    feedbacks,
  } = useReportStore();

  useEffect(() => {
    const feedbacksForSection = getFeedbacksBySection(section);
    if (feedbacksForSection.length > 0) {
      setLastFeedback(feedbacksForSection[feedbacksForSection.length - 1]);
    } else {
      setLastFeedback(null);
    }
  }, [feedbacks, section, getFeedbacksBySection, fetchReportFeedback]);

  const handleRatingChange = (value: number) => {
    setRating(value);
    setError("");
  };

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(event.target.value);
    setError("");
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (rating === 0 || feedback.trim() === "") {
      setError(t("fill_all_fields"));
    } else {
      sendReportFeedback({
        report_id,
        section,
        rating,
        thoughts: feedback,
      });

      setRating(0);
      setFeedback("");
    }
  };

  return (
    <Paper mt={40} p={24} radius={8} bg="#6F40E21A" className="like-response">
      {lastFeedback && (
        <Paper
          p={16}
          radius={8}
          bg="#F1F1F1"
          mb={24}
          withBorder
          bd="1px solid #A64DD7"
        >
          <Flex
            direction={matches ? "column" : "row"}
            align={matches ? "center" : "center"} // Center vertically on mobile
            justify={matches ? "flex-start" : "space-between"}
            gap={matches ? 12 : 8}
            style={{ width: "100%" }}
          >
            <Flex
              style={{ flex: 1, minWidth: 0 }}
              gap={10}
              direction={matches ? "column" : "row"}
              align={matches ? "center" : "center"} // Center vertically on mobile
            >
              <Text fw={600} color="#333">
                {t("last_feedback")}
              </Text>

              <Text
                color="#555"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={lastFeedback.thoughts}
              >
                {lastFeedback.thoughts}
              </Text>
            </Flex>
            <Rating
              value={lastFeedback.result_assessment}
              readOnly
              emptySymbol={<RatingIcon />}
              fullSymbol={<FullRatingIcon />}
              mt={matches ? 8 : 0}
            />
          </Flex>
        </Paper>
      )}
      <Flex
        gap={matches ? 16 : 24}
        align="center"
        direction={matches ? "column" : undefined}
      >
        <Text className="nowrap">{t("like_response")}</Text>
        <Rating
          value={rating}
          onChange={handleRatingChange}
          emptySymbol={<RatingIcon />}
          fullSymbol={<FullRatingIcon />}
        />
        <TextInput
          label={t("share_your_thoughts")}
          w="100%"
          value={feedback}
          onChange={handleFeedbackChange}
          onKeyDown={handleKeyPress}
        />
        {error && (
          <Text color="red" size="sm">
            {error}
          </Text>
        )}
      </Flex>
    </Paper>
  );
};
